<script setup lang="ts">
import { chunk, orderBy } from "lodash-es";
import { marked } from "marked";

import { computed } from "vue";
import rgaa from "../../criteres.json";
import { CriterionResultUserImpact, ReportCriteriumResult } from "../../types";
import {
  formatUserImpact,
  getUploadUrl,
  isTiptapDocumentEmpty
} from "../../utils";
import TiptapRenderer from "../tiptap/TiptapRenderer.vue";

const { error, indexes } = defineProps<{
  error: ReportCriteriumResult;
  indexes: {
    index: number;
    id: number;
  }[];
}>();

function getCriterium(topicNumber: number, criteriumNumber: number) {
  // FIXME: "any everywhere" : The criteria properties of each topic do not have the same signature. See: https://github.com/microsoft/TypeScript/issues/33591#issuecomment-786443978
  const criterium = (rgaa.topics as any)
    .find((t: any) => t.number === topicNumber)
    ?.criteria.find(
      (c: any) => c.criterium.number === criteriumNumber
    ).criterium;

  return criterium;
}

function getCriteriumTitle(topicNumber: number, criteriumNumber: number) {
  return marked.parseInline(getCriterium(topicNumber, criteriumNumber).title);
}

const sectionId = computed(() => `${error.pageId}_${error.topic}_${error.criterium}`);

const notCompliantItems = computed(() => {
  return orderBy(error.notCompliantItems, x => x.id);
});

function getLabelError(notCompliantItemId?: number) {
  return `Erreur ${indexes.find(x => x.id === notCompliantItemId)?.index}`;
}
</script>

<template>
  <div>
    <p :id="sectionId" class="fr-text--lg fr-text--bold criterium-title fr-mb-1w">
      {{ error.topic }}.{{ error.criterium }}&nbsp;–
      <span v-html="getCriteriumTitle(error.topic, error.criterium)" />
      <a :href="'#' + sectionId" class="fr-btn fr-icon-links-line fr-btn--tertiary-no-outline fr-btn--sm">
        <span class="fr-sr-only">ancre vers le critère {{ error.topic }}.{{ error.criterium }}</span>
      </a>
    </p>

    <p class="fr-badge fr-badge--lg fr-badge--error fr-badge--no-icon fr-mb-3w">
      non-conformité ({{ notCompliantItems.length }})
    </p>

    <ul role="list" class="fr-raw-list">
      <li
        v-for="(notCompliantItem, index) in notCompliantItems"
        :key="index"
        class="criterium-not-compliant-item"
      >
        <div :id="`${sectionId}_${notCompliantItem.id}`" class="criterium-not-compliant-item-header">
          <ul v-if="notCompliantItem.userImpact || notCompliantItem.quickWin" class="criterium-not-compliant-item-badges fr-badges-group fr-mb-2w">
            <li v-if="notCompliantItem.userImpact">
              <p
                class="fr-badge fr-badge--sm fr-m-1-5v"
                :class="{
                  'fr-badge--yellow-moutarde':
                    notCompliantItem.userImpact
                    === CriterionResultUserImpact.MAJOR,
                  'fr-badge--error fr-badge--no-icon':
                    notCompliantItem.userImpact
                    === CriterionResultUserImpact.BLOCKING
                }"
              >
                Impact {{ formatUserImpact(notCompliantItem.userImpact) }}
              </p>
            </li>
            <li v-if="notCompliantItem.quickWin">
              <p class="fr-badge fr-badge--sm fr-m-1-5v">Facile à corriger</p>
            </li>
          </ul>

          <a :href="`#${sectionId}_${notCompliantItem.id}`" class="fr-btn fr-icon-links-line fr-btn--tertiary-no-outline fr-btn--sm">
            <span class="fr-sr-only">ancre vers l'erreur {{ index + 1 }} du critère {{ error.topic }}.{{ error.criterium }}</span>
          </a>
        </div>

        <h3 class="fr-mb-3v">
          <span class="fr-sr-only">{{ getLabelError(notCompliantItem.id) }}</span>
          <span v-if="notCompliantItem.title" class="fr-sr-only"> : </span>
          <span v-if="notCompliantItem.title">
            {{ notCompliantItem.title }}
          </span>
        </h3>

        <!-- Error -->
        <TiptapRenderer
          v-if="
            notCompliantItem.comment &&
              !isTiptapDocumentEmpty(notCompliantItem.comment)
          "
          :key="error.topic + '.' + error.criterium"
          :document="notCompliantItem.comment"
        />
        <p v-else class="criterium-not-compliant-item-no-erreur">
          Aucune description de l’erreur ou recommandation ajoutée par l’auditrice ou l’auditeur.
        </p>

      </li>

    </ul>

    <template v-if="chunk(error.exampleImages, 2).length">
      <p class="fr-text--xs fr-mb-1w error-accordion-subtitle">
        Exemple(s) d’erreur(s)
      </p>
      <div class="fr-container--fluid">
        <div
          v-for="(line, k) in chunk(error.exampleImages, 2)"
          :key="k"
          class="fr-grid-row fr-grid-row--gutters"
        >
          <a
            v-for="example in line"
            :key="example.key"
            class="fr-col-md-6 fr-col-12 image-link"
            :href="getUploadUrl(example.key)"
            target="_blank"
          >
            <span class="fr-sr-only">
              Ouvrir l’image dans une nouvelle fenêtre
            </span>
            <img
              class="example-image"
              :src="getUploadUrl(example.key)"
              alt=""
            />
          </a>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.example-image {
  max-height: 12.5rem;
  max-width: calc(100% - 1.5rem); /* Full width minus externa icon size */
  width: auto;
}

.criterium-title {
  scroll-margin: 4rem;
}

.criterium-not-compliant-item {
  border: 1px solid var(--border-default-grey);
  padding: 1.25rem 1.5rem;
  margin-block-end: 1.5rem;
}

.criterium-not-compliant-item :deep(h4) {
  font-size: 1.375rem;
}

.criterium-not-compliant-item :deep(h5) {
  font-size: 1.25rem;
}

.criterium-not-compliant-item :deep(h6) {
  font-size: 1.125rem;
}

.criterium-not-compliant-item :deep(.tiptap--rendered p:last-child) {
  margin-block-end: 0;
}

.criterium-not-compliant-item :deep(.tiptap--rendered ul) {
  list-style: disc;
}

.criterium-not-compliant-item :deep(.tiptap--rendered ul),
.criterium-not-compliant-item :deep(.tiptap--rendered ol) {
  margin-inline-start: 2rem;
  margin-block-end: 1.5rem;
}

.criterium-not-compliant-item-badges {
  margin-inline-end: auto;
}

.criterium-not-compliant-item-header {
  display: flex;
  justify-content: end;
}

.criterium-not-compliant-item-no-erreur {
  font-style: italic;
  color: var(--text-mention-grey);
  text-align: center;
}
</style>
